/**
 * Add many terms at once: paste one per line, check what was understood, add
 * the good ones in a single press.
 *
 * Shares `parseTerms` with the add row of `TermList`, so a term is accepted or
 * refused the same way however it arrives. Duplicates and invalid lines are
 * shown, not silently dropped, and are left out of the count on the button.
 *
 * @module components/ui/PasteListDialog
 */

import React, { useState } from "react";

import { Badge } from "./Badge";
import { Button } from "./Button";
import { useConfirm } from "./ConfirmProvider";
import { FormField, TextAreaField } from "./Form";
import { Icon } from "./Icon";
import { Modal, type ModalGuard } from "./Modal";
import { type ParsedTerm, parseTerms } from "./term-list";

export interface PasteListDialogProps {
  open: boolean;
  onClose: () => void;
  /** What one term is, singular and lowercase: "discipline". */
  noun: string;
  /** Terms already in the list, which a pasted line may not repeat. */
  existing: readonly string[];
  /** Persists the accepted terms. Resolves to whether they were kept. */
  onAdd: (terms: string[]) => Promise<boolean>;
  "data-testid"?: string;
}

const FLAG: Record<
  Exclude<ParsedTerm["status"], "ok">,
  { tone: "warning" | "error"; icon: string }
> = {
  duplicate: { tone: "warning", icon: "copy" },
  invalid: { tone: "error", icon: "warning-circle" },
};

/**
 * @example
 * ```tsx
 * <PasteListDialog open={open} onClose={close} noun="discipline" existing={list} onAdd={save} />
 * ```
 */
export function PasteListDialog({
  open,
  onClose,
  noun,
  existing,
  onAdd,
  "data-testid": testId,
}: PasteListDialogProps): React.ReactElement {
  const confirm = useConfirm();
  const [text, setText] = useState("");
  const [pending, setPending] = useState(false);
  const parsed = parseTerms(text, existing);
  const accepted = parsed.filter((row) => row.status === "ok");

  // Escape, the backdrop and the close button ask before dropping a paste.
  const guard: ModalGuard =
    text.trim() !== "" && !pending
      ? {
          dirty: true,
          onConfirmDiscard: () =>
            confirm({
              title: "Discard the pasted list?",
              confirmLabel: "Discard list",
              cancelLabel: "Keep editing",
              tone: "danger",
              "data-testid": testId ? `${testId}-discard-confirmation` : undefined,
            }),
        }
      : {};

  const close = (): void => {
    setText("");
    onClose();
  };

  const add = (): void => {
    setPending(true);
    onAdd(accepted.map((row) => row.term))
      .then((kept) => {
        setPending(false);
        if (kept) {
          close();
        }
      })
      .catch(() => {
        setPending(false);
      });
  };

  return (
    <Modal
      open={open}
      onClose={close}
      title={`Paste a list of ${noun}s`}
      subtitle="One per line. Bullets and numbering are stripped."
      closeRefusal={pending ? "Adding is in progress." : undefined}
      footer={
        <>
          <Button variant="outline" onClick={close} disabled={pending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={add}
            loading={pending}
            disabled={accepted.length === 0}
            disabledReason={accepted.length === 0 ? `Nothing to add yet.` : undefined}
            data-testid={testId ? `${testId}-add` : undefined}
          >
            {`Add ${String(accepted.length)}`}
          </Button>
        </>
      }
      data-testid={testId}
      {...guard}
    >
      <FormField
        label={`${noun[0]?.toUpperCase() ?? ""}${noun.slice(1)} names`}
        htmlFor="paste-list-input"
      >
        <TextAreaField
          id="paste-list-input"
          value={text}
          onChange={setText}
          rows={6}
          placeholder={`Data Science\nPhysics\nMathematics`}
        />
      </FormField>
      {parsed.length > 0 ? (
        <ul className="mt-4 flex flex-col gap-1" aria-label="What was understood">
          {parsed.map((row, index) => (
            <li
              // A line has no identity beyond its position in the paste.
              key={`${String(index)}-${row.term}`}
              className="flex items-center justify-between gap-3 text-sm"
            >
              <span className={row.status === "ok" ? "" : "text-muted line-through"}>
                {row.term}
              </span>
              {row.status === "ok" ? (
                <Icon name="check" className="text-success-strong" aria-label="Will be added" />
              ) : (
                <Badge tone={FLAG[row.status].tone} size="sm" className="gap-1">
                  <Icon name={FLAG[row.status].icon} aria-hidden />
                  {row.reason}
                </Badge>
              )}
            </li>
          ))}
        </ul>
      ) : null}
    </Modal>
  );
}
